import { useState } from "react";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useCompra } from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Download,
  Copy,
  Check,
  FileText,
  Clock,
  DollarSign,
  CreditCard,
  ArrowLeft,
  FlaskConical,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import jsPDF from "jspdf";
import {
  METODO_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  VEICULO_TIPO_LABELS,
} from "@/lib/constants";
import { useLocation } from "wouter";
import { useModulos } from "@/hooks/use-modulos";

export default function ComprovanteCredito({ id }: { id: string }) {
  const { profile } = useAuth();
  const { modulos, isLoading: modulosLoading } = useModulos();
  const [, setLocation] = useLocation();
  const { data: compra, isLoading, error } = useCompra(id);
  const { toast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);

  if (!modulosLoading && !modulos.creditosPrePagos) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <CreditCard className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Módulo não disponível</h2>
          <p className="text-muted-foreground max-w-sm">
            O módulo de Créditos Pré-Pagos não está ativo para sua vistoriadora.
          </p>
        </div>
      </Shell>
    );
  }

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
    toast({
      title: "Copiado",
      description: `${label} copiado para a área de transferência`,
    });
  };

  const handleDownloadPDF = () => {
    if (!compra) return;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    let yPosition = margin;

    // Header
    pdf.setFontSize(24);
    pdf.setTextColor(0, 51, 102);
    pdf.text("COMPROVANTE DE COMPRA", margin, yPosition);

    yPosition += 15;

    // Separator line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 10;

    // Compra Details
    pdf.setFontSize(11);
    pdf.setTextColor(50, 50, 50);

    const details = [
      ["ID da Compra:", compra.id],
      [
        "Data:",
        format(new Date(compra.data), "dd/MM/yyyy HH:mm", { locale: ptBR }),
      ],
      ["Status:", STATUS_LABELS[compra.status] || compra.status],
      ["Quantidade de Créditos:", compra.quantidade.toString()],
      ["Tipo de Veículo:", VEICULO_TIPO_LABELS[compra.tipo_veiculo]],
    ];

    details.forEach(([label, value]) => {
      pdf.setFont(pdf.getFont().fontName, "bold");
      pdf.text(label, margin, yPosition);
      pdf.setFont(pdf.getFont().fontName, "normal");
      pdf.text(value, margin + 50, yPosition);
      yPosition += 8;
    });

    yPosition += 8;

    // Valor section with highlight
    pdf.setFillColor(240, 245, 250);
    pdf.rect(margin, yPosition, contentWidth, 15, "F");

    pdf.setFontSize(12);
    pdf.setFont(pdf.getFont().fontName, "bold");
    pdf.setTextColor(0, 51, 102);
    pdf.text("VALOR TOTAL", margin + 5, yPosition + 6);

    pdf.setFontSize(16);
    pdf.text(
      `R$ ${compra.valor_total.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
      })}`,
      pageWidth - margin - 5,
      yPosition + 10,
      { align: "right" },
    );

    yPosition += 20;

    // Payment method
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 10;

    pdf.setFontSize(11);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont(pdf.getFont().fontName, "bold");
    pdf.text("MÉTODO DE PAGAMENTO", margin, yPosition);

    yPosition += 8;

    pdf.setFont(pdf.getFont().fontName, "normal");
    pdf.text(
      METODO_LABELS[compra.metodo] || compra.metodo,
      margin + 5,
      yPosition,
    );

    if (compra.asaas_payment_id) {
      yPosition += 8;
      pdf.setFont(pdf.getFont().fontName, "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(
        `ID Transação Asaas: ${compra.asaas_payment_id}`,
        margin + 5,
        yPosition,
      );
    }

    yPosition += 15;

    // Footer
    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      "Este comprovante foi gerado automaticamente e é válido como prova de compra.",
      margin,
      pageHeight - 20,
    );
    pdf.text(
      `Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}`,
      margin,
      pageHeight - 15,
    );

    pdf.save(`comprovante-creditos-${compra.id.slice(0, 8)}.pdf`);
    toast({
      title: "Sucesso",
      description: "Comprovante baixado com sucesso",
    });
  };

  if (error) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-red-600 font-semibold">
            Erro ao carregar comprovante
          </p>
          <p className="text-muted-foreground mt-2">{error.message}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/creditos")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Comprovante de Compra
            </h1>
            <p className="text-muted-foreground mt-2">
              Detalhes da sua compra de créditos
            </p>
          </div>
          <Button
            onClick={handleDownloadPDF}
            disabled={isLoading}
            className="gap-2"
            size="lg"
          >
            <Download className="w-4 h-4" />
            Baixar PDF
          </Button>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </CardContent>
          </Card>
        ) : compra ? (
          <>
            {/* Main Info Card */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Informações da Compra
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_COLORS[compra.status] || ""}>
                      {STATUS_LABELS[compra.status]}
                    </Badge>
                    {compra.metodo === "simulado_teste" && (
                      <Badge className="bg-purple-100 text-purple-700 gap-1">
                        <FlaskConical className="w-3 h-3" />
                        Simulado
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* ID and Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      ID da Compra
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <code className="text-sm font-mono bg-slate-100 px-3 py-2 rounded flex-1 truncate">
                        {compra.id}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyToClipboard(compra.id, "ID")}
                      >
                        {copied === "ID" ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground">
                      Data da Compra
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="font-medium">
                        {format(new Date(compra.data), "dd/MM/yyyy HH:mm", {
                          locale: ptBR,
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Créditos e Veículo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Quantidade de Créditos
                    </p>
                    <p className="text-2xl font-bold mt-2">
                      {compra.quantidade}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {compra.quantidade} laudo(s)
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground">
                      Tipo de Veículo
                    </p>
                    <p className="text-lg font-semibold mt-2">
                      {VEICULO_TIPO_LABELS[compra.tipo_veiculo]}
                    </p>
                  </div>
                </div>

                {/* Valor */}
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-primary" />
                      <span className="text-muted-foreground">Valor Total</span>
                    </div>
                    <span className="text-3xl font-bold text-primary">
                      R${" "}
                      {compra.valor_total.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  Método de Pagamento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Forma de Pagamento
                  </p>
                  <p className="text-lg font-semibold mt-2">
                    {METODO_LABELS[compra.metodo] || compra.metodo}
                  </p>
                </div>

                {compra.asaas_payment_id && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      ID da Transação Asaas
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <code className="text-sm font-mono bg-slate-100 px-3 py-2 rounded flex-1 truncate">
                        {compra.asaas_payment_id}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleCopyToClipboard(
                            compra.asaas_payment_id!,
                            "ID Transação",
                          )
                        }
                      >
                        {copied === "ID Transação" ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions Card */}
            <Card className="bg-slate-50">
              <CardHeader>
                <CardTitle>Próximas Ações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  ✅ Compra registrada com sucesso
                </p>
                {compra.status === "pago" && (
                  <p className="text-sm text-green-700">
                    ✅ Créditos ativados e disponíveis para uso imediato
                  </p>
                )}
                {compra.status === "processando" && (
                  <p className="text-sm text-blue-700">
                    ⏳ Processamento em andamento. Sua compra será confirmada em
                    breve.
                  </p>
                )}
                {compra.status === "falhou" && (
                  <p className="text-sm text-red-700">
                    ❌ Esta transação falhou. Entre em contato com o suporte.
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Comprovante não encontrado</p>
          </div>
        )}
      </div>
    </Shell>
  );
}
